import { useEffect, useRef, useState, type CSSProperties, type DragEvent, type HTMLAttributes, type MouseEvent, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';
import {
  busyDays,
  dayNumber,
  detailsLabel,
  eventName,
  eventPieces,
  uniqueEvents,
  withDates,
  eventDays,
  layoutWeek,
  monthKind,
  rentalDays,
  sortDots,
  MONTHS,
  MONTHS_GENITIVE,
  monthGrid,
  splitWeekEvents,
  startOfWeek,
  titleDates,
  weekHours,
  WEEKDAYS_SHORT,
  type CalendarEvent,
  type DatedEvent,
  type RentalDay,
  type TimedPlacement,
  type WeekSegment,
} from './calendar';
import { addDays, EVENT_STATUSES, isClosed, settlementMarker, warsawParts } from './labels';
import { initials } from '../crew/types';
import { copyModifier, isMacLike } from './shortcuts';
import { DialogLayer } from '../ui/Dialog';
import { dotColor, markStyle } from '../contractors/colors';

/**
 * P6: what a view lets the person do with days and events, each only when the page gives it: a click selects a
 * day (for ⌘V and the arrows), a double click asks for a new event on it (owner remark 7), and with `drag` the
 * events can be dragged onto another day — the page moves them, or copies them with ⌥ (owner remark 28).
 */
export type Interact = {
  selectedDay?: string | null | undefined;
  /** Days picked with Shift / Cmd (owner 2026-10-21 00:44: one event of several terms); marked like the selected day. */
  selectedDays?: readonly string[] | undefined;
  /** A click on a day: `range` with Shift, `toggle` with Cmd (Mac) or Ctrl. */
  onSelectDay?: ((day: string, mods: { range: boolean; toggle: boolean }) => void) | undefined;
  onNewOnDay?: ((day: string) => void) | undefined;
  drag?: { onDrop: (eventId: string, day: string, copy: boolean) => void } | undefined;
  /**
   * Owner 2026-10-22 14:40 (the dates of a new event / rental open in the panel): pressing on a day and moving over
   * others picks the run from that day, live while the mouse moves; `add` with Cmd (Mac) / Ctrl held at the press.
   */
  onPickRange?: ((from: string, to: string, add: boolean) => void) | undefined;
  /**
   * Review I2 / M1 (dates of a new event being picked): a click on an event — a bar, a timed event, a day of the year
   * with events — picks its day like a click on the day (Shift / Cmd too) and never opens the card; the event's name
   * stays as the tooltip.
   */
  pickOnly?: boolean | undefined;
  /** „Oferta łączona” (owner 2026-10-30): ⌘-click (Ctrl) on an event picks it instead of opening it; picked ones are marked. */
  onToggleEvent?: ((eventId: string) => void) | undefined;
  pickedEvents?: readonly string[] | undefined;
};

/** A ⌘-click (Ctrl) on an event that picks it for an offer (not while the dates of a new event are picked). */
function toggledEvent(ev: MouseEvent<HTMLElement>, id: string, i: Interact): boolean {
  if (i.pickOnly || !i.onToggleEvent || !(isMacLike() ? ev.metaKey : ev.ctrlKey)) return false;
  ev.stopPropagation();
  i.onToggleEvent(id);
  return true;
}
const pickedClass = (id: string, i: Interact) => (i.pickedEvents?.includes(id) ? ' picked' : '');

/**
 * A dot row of the month grid that is not an event (owner 2026-10-29 16:02: „wszystkie zadania do wykonania, które nie
 * są wydarzeniami, są kropkami”): its time ('' for none), name, the client's colour key (none: grey), what it says.
 */
export type DayDot = { key: string; time: string; name: string; color: string | undefined; label: string; title: string; done?: boolean; overdue?: boolean; onOpen: () => void };

/**
 * What a view may draw on a day besides events: the company tasks layer (P7, `company-tasks/CalendarTasks`).
 * `marks`: clickable marks of the day (the week's head, the phone); `dots`: the month's dot rows; `has`: a dot in the
 * phone's month grid.
 */
export type DayExtra = { marks: (day: string) => ReactNode; dots?: ((day: string) => DayDot[]) | undefined; has: (day: string) => boolean };

/** `day`: the day clicked in the calendar to open the event (owner 2026-10-25: the page marks that day). */
type OnOpen = (id: string, day?: string) => void;

/** Views take every listed event and place only those whose dates the role may see. */
type Common = { events: CalendarEvent[]; today: string; selectedId: string | null; onOpen: OnOpen; dayExtra?: DayExtra | undefined } & Interact;

/** The event being dragged (a drop on a day of the same page reads it; the data transfer carries it too). */
let dragged: string | null = null;
/** Days from the event's first day to the dragged term's (an event of several terms): the event moves as the term does. */
let draggedOffset = 0;

function dragSource(id: string, i: Interact, offsetDays = 0): HTMLAttributes<HTMLElement> & { draggable?: boolean } {
  if (!i.drag) return {};
  return {
    draggable: true,
    onDragStart: (e: DragEvent<HTMLElement>) => {
      dragged = id;
      draggedOffset = offsetDays;
      if (e.dataTransfer) {
        e.dataTransfer.effectAllowed = 'copyMove';
        e.dataTransfer.setData('text/plain', id);
      }
    },
    onDragEnd: () => {
      dragged = null;
      draggedOffset = 0;
    },
  };
}

/** A drag over days under way (`onPickRange`): where it started, the day it is over, whether it adds. */
let rangeDrag: { from: string; to: string; add: boolean; moved: boolean; pick: (from: string, to: string, add: boolean) => void } | null = null;
/** A drag over days just ended: the click the browser sends after it is not a click on a day. */
let justDragged = false;

/** The day under the pointer: the day cell below an event bar too (the bars lie over the cells). */
function dayUnder(e: { clientX: number; clientY: number; target: EventTarget | null }): string | null {
  const hit = typeof document.elementsFromPoint === 'function' && Number.isFinite(e.clientX) ? document.elementsFromPoint(e.clientX, e.clientY).find((el) => el instanceof HTMLElement && el.dataset.day) : undefined;
  const el = hit ?? (e.target instanceof Element ? e.target.closest('[data-day]') : null);
  return el instanceof HTMLElement ? (el.dataset.day ?? null) : null;
}

function startRangeDrag(day: string, e: ReactPointerEvent<HTMLElement>, pick: (from: string, to: string, add: boolean) => void) {
  // the left button only, a mouse or a pen (a finger scrolls); Shift-click is the run from the anchor; a bar is dragged itself
  if ((e.button ?? 0) !== 0 || e.shiftKey || e.pointerType === 'touch') return;
  // a bar is dragged itself; a button inside a day (a task mark, "+N więcej") or a link is its own click
  if (e.target instanceof Element) {
    if (e.target.closest('[data-event-id], a, input')) return;
    const button = e.target.closest('button');
    if (button && button !== e.currentTarget) return;
  }
  rangeDrag = { from: day, to: day, add: isMacLike() ? e.metaKey : e.ctrlKey, moved: false, pick };
  const move = (ev: PointerEvent) => {
    const d = rangeDrag && dayUnder(ev);
    if (!rangeDrag || !d || d === rangeDrag.to) return;
    rangeDrag.to = d;
    rangeDrag.moved = true;
    rangeDrag.pick(rangeDrag.from, d, rangeDrag.add);
  };
  const end = () => {
    // any move counts (review M2): also a drag that came back to its first day
    if (rangeDrag?.moved) {
      justDragged = true;
      setTimeout(() => (justDragged = false), 0);
    }
    rangeDrag = null;
    document.removeEventListener('pointermove', move);
    document.removeEventListener('pointerup', end);
    document.removeEventListener('pointercancel', end);
  };
  document.addEventListener('pointermove', move);
  document.addEventListener('pointerup', end);
  document.addEventListener('pointercancel', end);
}

/** Whether the day is the selected one or among the days picked with Shift / Cmd. */
const isPicked = (day: string, i: Interact) => day === i.selectedDay || (i.selectedDays?.includes(day) ?? false);

/**
 * A day cell: selected by a click (Shift: a run of days, Cmd / Ctrl: one more day), a new event by a double click, a
 * drop target for dragged events.
 */
function dayTarget(day: string, i: Interact): HTMLAttributes<HTMLElement> & { 'data-day': string } {
  const drag = i.drag;
  const mac = isMacLike();
  return {
    'data-day': day,
    ...(i.onSelectDay
      ? {
          onClick: (e: MouseEvent<HTMLElement>) => {
            if (!justDragged) i.onSelectDay!(day, { range: e.shiftKey, toggle: mac ? e.metaKey : e.ctrlKey });
          },
          // Shift-click must not select the text of the days
          onMouseDown: (e: MouseEvent<HTMLElement>) => {
            if (e.shiftKey) e.preventDefault();
          },
        }
      : {}),
    ...(i.onPickRange ? { onPointerDown: (e: ReactPointerEvent<HTMLElement>) => startRangeDrag(day, e, i.onPickRange!) } : {}),
    ...(i.onNewOnDay ? { onDoubleClick: () => i.onNewOnDay!(day) } : {}),
    ...(drag
      ? {
          onDragOver: (e: DragEvent<HTMLElement>) => {
            e.preventDefault();
            if (e.dataTransfer) e.dataTransfer.dropEffect = copyModifier({ altKey: !!e.altKey, ctrlKey: !!e.ctrlKey }, isMacLike()) ? 'copy' : 'move';
          },
          onDrop: (e: DragEvent<HTMLElement>) => {
            e.preventDefault();
            const id = dragged ?? e.dataTransfer?.getData('text/plain') ?? '';
            const offset = dragged ? draggedOffset : 0;
            dragged = null;
            draggedOffset = 0;
            if (id) drag.onDrop(id, addDays(day, -offset), copyModifier({ altKey: !!e.altKey, ctrlKey: !!e.ctrlKey }, isMacLike()));
          },
        }
      : {}),
  };
}

function timeOf(iso: string): string {
  const t = warsawParts(iso).time;
  return t === '00:00' ? '' : t;
}

/** One event as a bar: status sets the fill, a yellow edge marks "potrzebna wycena". */
/** "Zaznacz dzień (wydarzenie: X)": what an event says while a click on it picks its day (review I2). */
const pickLabel = (e: CalendarEvent) => `Zaznacz dzień (wydarzenie: ${eventName(e)})`;
const modsOf = (ev: MouseEvent<HTMLElement>) => ({ range: ev.shiftKey, toggle: isMacLike() ? ev.metaKey : ev.ctrlKey });

function EventBar({ seg, week, selected, onOpen, showTime = true, interact = {} }: { seg: WeekSegment; week: readonly string[]; selected: boolean; onOpen: OnOpen; showTime?: boolean; interact?: Interact }) {
  const e = seg.event;
  const time = showTime && !seg.continuesBefore ? timeOf(e.startsAt) : '';
  // one term of an event of several terms (owner 2026-10-21 00:44): "1/2" on the bar
  const term = e.piece ? `${e.piece.index + 1}/${e.piece.count}` : '';
  return (
    <button
      type="button"
      className={`cal-bar${seg.continuesBefore ? ' cont-before' : ''}${seg.continuesAfter ? ' cont-after' : ''}${e.needsQuote ? ' needs-quote' : ''}${selected ? ' selected' : ''}${term ? ' term' : ''}${e.clientColor ? ' client-mark' : ''}${pickedClass(e.id, interact)}`}
      data-status={e.status}
      data-kind={e.kind}
      data-event-id={e.id}
      {...dragSource(e.id, interact, e.piece?.offsetDays)}
      style={{ gridColumn: `${seg.startCol + 1} / span ${seg.span}`, gridRow: seg.lane + 2, ...(e.clientColor ? markStyle(e.clientColor) : {}) }}
      aria-label={interact.pickOnly ? pickLabel(e) : `${detailsLabel(e)}${e.piece ? `, termin ${e.piece.index + 1} z ${e.piece.count}` : ''}`}
      // owner 2026-10-22 01:04: the title with its date(s)
      title={`${eventName(e)} · ${titleDates(e)}${term ? ` (termin ${term})` : ''}${e.unstaffed ? ' · nieobsadzone' : ''}${settlementMarker(e.settlement) ? ` · ${settlementMarker(e.settlement)}` : ''}`}
      onClick={(ev) => {
        // the day under the mouse; from the keyboard (no pointer) the bar's first day in this row
        const under = dayUnder(ev);
        const day = under && week.includes(under) ? under : week[seg.startCol]!;
        if (toggledEvent(ev, e.id, interact)) return;
        if (!interact.pickOnly) return onOpen(e.id, day);
        if (justDragged) return;
        interact.onSelectDay?.(day, modsOf(ev));
      }}
    >
      {time && <span className="cal-bar-time">{time}</span>}
      <Unstaffed on={e.unstaffed} />
      <span className="cal-bar-name">{eventName(e)}</span>
      {term && <span className="cal-bar-term">{term}</span>}
      {/* a one-day bar of the month grid is too narrow for more than the name */}
      {seg.span > 1 && <CrewInitials crew={e.crew} />}
    </button>
  );
}

/** „Nieobsadzone” on a bar: a small red mark before the name (the words are in the bar's title and label). */
function Unstaffed({ on }: { on: boolean | undefined }) {
  return on ? <span className="cal-bar-unstaffed" aria-hidden="true" data-feature-id="crew.people_availability" /> : null;
}

/** The crew on a bar as initials ("JK, ON"); the full names are in the bar's title and on the event card. */
function CrewInitials({ crew }: { crew: string[] | undefined }) {
  const text = crew ? [...new Set(crew.map(initials).filter(Boolean))].join(', ') : '';
  return text ? (
    <span className="cal-bar-crew" aria-hidden="true" data-feature-id="crew.calendar">
      {text}
    </span>
  ) : null;
}

/**
 * Lines of a month day under its number (owner 2026-10-29: Apple-like, thinner tiles — more fit): the event tiles first,
 * then the dot rows; what does not fit is counted in „+N więcej” on the next line.
 */
const MAX_LINES = 4;

/** A rental on one day of the month grid: a dot in the client's colour (grey without one), the name, the time on its first day. */
function RentalDotRow({ r, day, col, line, selected, onOpen, interact }: { r: RentalDay; day: string; col: number; line: number; selected: boolean; onOpen: OnOpen; interact: Interact }) {
  const e = r.event;
  const time = r.first ? timeOf(e.startsAt) : '';
  const cancelled = isClosed(e.status);
  const status = e.status ? EVENT_STATUSES[e.status] : '';
  return (
    <button
      type="button"
      className={`cal-dot-row${cancelled ? ' cancelled' : ''}${selected ? ' selected' : ''}${pickedClass(e.id, interact)}`}
      data-status={e.status}
      data-kind={e.kind}
      data-event-id={e.id}
      data-dot-day={day}
      {...dragSource(e.id, interact, r.offsetDays)}
      style={{ gridColumn: col + 1, gridRow: line + 2 }}
      aria-label={interact.pickOnly ? pickLabel(e) : `${detailsLabel(e)}${e.piece ? `, termin ${e.piece.index + 1} z ${e.piece.count}` : ''}`}
      // the status is not drawn on a dot: it is in the title (owner 2026-10-29: „kropka odpowiada kolorowi klienta”)
      title={[`Wypożyczenie: ${eventName(e)}`, titleDates(e), status, e.needsQuote ? 'potrzebna wycena' : '', settlementMarker(e.settlement) ?? ''].filter(Boolean).join(' · ')}
      onClick={(ev) => {
        if (toggledEvent(ev, e.id, interact)) return;
        if (!interact.pickOnly) return onOpen(e.id, day);
        if (justDragged) return;
        interact.onSelectDay?.(day, modsOf(ev));
      }}
    >
      <span className="cal-dot" aria-hidden="true" style={{ background: dotColor(e.clientColor) }} />
      <span className="cal-dot-name">{eventName(e)}</span>
      {e.needsQuote && <span className="cal-dot-quote" aria-hidden="true" />}
      {time && <span className="cal-dot-time">{time}</span>}
    </button>
  );
}

/** A dot row of the day's extras (a company task): its own click. */
function ExtraDotRow({ d, col, line }: { d: DayDot; col: number; line: number }) {
  return (
    <button
      type="button"
      className={`cal-dot-row extra${d.done ? ' done' : ''}${d.overdue ? ' overdue' : ''}`}
      style={{ gridColumn: col + 1, gridRow: line + 2 }}
      aria-label={d.label}
      title={d.title}
      onClick={d.onOpen}
    >
      <span className="cal-dot" aria-hidden="true" style={{ background: dotColor(d.color) }} />
      <span className="cal-dot-name">{d.name}</span>
      {d.time && <span className="cal-dot-time">{d.time}</span>}
    </button>
  );
}

type MonthDot = { key: string; time: string; name: string; render: (col: number, line: number) => ReactNode };

/**
 * Month: weeks as rows. Owner 2026-10-29 16:02 (like Apple Calendar): events are tiles running across the days they
 * cover; rentals (on every day of them) and company tasks are dot rows under the tiles, in time order; what does not
 * fit is „+N więcej”, opening that week.
 */
export function MonthView({ anchor, events: all, today, selectedId, onOpen, onPickDay, dayExtra, ...interact }: Common & { anchor: string; onPickDay: (day: string) => void }) {
  const events = eventPieces(withDates(all));
  const tiles = events.filter((e) => monthKind(e) === 'tile');
  const rentals = events.filter((e) => monthKind(e) === 'dot');
  const month = anchor.slice(0, 7);
  const weeks = monthGrid(anchor);
  return (
    <div className="cal-month" role="region" aria-label="Miesiąc">
      <div className="cal-weekdays" aria-hidden="true">
        {WEEKDAYS_SHORT.map((d) => (
          <div key={d}>{d}</div>
        ))}
      </div>
      {weeks.map((week) => {
        const segs = layoutWeek(week, tiles);
        const shown = segs.filter((s) => s.lane < MAX_LINES);
        const byDay = rentalDays(week, rentals);
        const days = week.map((day, col) => {
          const covering = segs.filter((s) => col >= s.startCol && col < s.startCol + s.span);
          const top = Math.max(0, ...covering.filter((s) => s.lane < MAX_LINES).map((s) => s.lane + 1));
          const hiddenTiles = covering.filter((s) => s.lane >= MAX_LINES).length;
          const dots: MonthDot[] = sortDots([
            ...(byDay.get(day) ?? []).map((r) => ({
              key: `r:${r.event.id}:${r.event.piece?.index ?? 0}:${day}`,
              time: r.first ? timeOf(r.event.startsAt) : '',
              name: eventName(r.event),
              render: (c: number, line: number) => <RentalDotRow key={`r:${r.event.id}:${r.event.piece?.index ?? 0}:${day}`} r={r} day={day} col={c} line={line} selected={r.event.id === selectedId} onOpen={onOpen} interact={interact} />,
            })),
            ...(dayExtra?.dots?.(day) ?? []).map((d) => ({ key: `x:${d.key}`, time: d.time, name: d.name, render: (c: number, line: number) => <ExtraDotRow key={`x:${d.key}:${day}`} d={d} col={c} line={line} /> })),
          ]);
          const visible = dots.slice(0, Math.max(0, MAX_LINES - top));
          return { top, visible, hidden: dots.length - visible.length + hiddenTiles };
        });
        return (
          <div className="cal-week" key={week[0]}>
            <div className="cal-days" aria-hidden="true">
              {week.map((day) => (
                <div key={day} className={`cal-day${day.slice(0, 7) !== month ? ' outside' : ''}${day === today ? ' today' : ''}${isPicked(day, interact) ? ' selected-day' : ''}`} {...dayTarget(day, interact)}>
                  <span className="cal-day-num">{dayNumber(day)}</span>
                </div>
              ))}
            </div>
            <div className="cal-lanes">
              {shown.map((seg) => (
                <EventBar key={`${seg.event.id}:${seg.event.piece?.index ?? 0}`} seg={seg} week={week} selected={seg.event.id === selectedId} onOpen={onOpen} interact={interact} />
              ))}
              {days.flatMap(({ top, visible }, col) => visible.map((d, i) => d.render(col, top + i)))}
              {days.map(({ top, visible, hidden }, col) =>
                hidden > 0 ? (
                  <button
                    key={`more:${col}`}
                    type="button"
                    className="cal-more"
                    style={{ gridColumn: col + 1, gridRow: top + visible.length + 2 }}
                    aria-label={`+${hidden} więcej – pokaż tydzień z ${dayNumber(week[col]!)} ${MONTHS_GENITIVE[Number(week[col]!.slice(5, 7)) - 1]}`}
                    onClick={() => onPickDay(week[col]!)}
                  >
                    +{hidden} więcej
                  </button>
                ) : null,
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

const HOUR_PX = 44;

/** Side-by-side columns for timed events that overlap within one day. */
function columns(items: TimedPlacement[]): { item: TimedPlacement; col: number; cols: number }[] {
  const sorted = [...items].sort((a, b) => a.startMin - b.startMin);
  const ends: number[] = [];
  const placed = sorted.map((item) => {
    let col = ends.findIndex((end) => end <= item.startMin);
    if (col === -1) col = ends.length;
    ends[col] = item.endMin;
    return { item, col };
  });
  return placed.map((p) => ({ ...p, cols: ends.length }));
}

/** Week: a band of whole-day and multi-day bars on top, timed one-day events on an hour grid below. */
export function WeekView({ anchor, events: all, today, selectedId, onOpen, dayExtra, ...interact }: Common & { anchor: string }) {
  const events = eventPieces(withDates(all));
  const start = startOfWeek(anchor);
  const week = Array.from({ length: 7 }, (_, i) => addDays(start, i));
  const { allDay, timed } = splitWeekEvents(events);
  const segs = layoutWeek(week, allDay);
  const lanes = segs.reduce((n, s) => Math.max(n, s.lane + 1), 0);
  const scroller = useRef<HTMLDivElement>(null);
  // 6:00–24:00, or from earlier when an event of this week starts before 6:00 (events are never cut off)
  const { from: HOUR_FROM, to: HOUR_TO } = weekHours(timed);
  const firstHour = Math.min(8, ...timed.map((t) => Math.floor(t.startMin / 60)));
  useEffect(() => {
    // open on the working day (8:00), or on the earliest event when one starts before that
    if (scroller.current) scroller.current.scrollTop = (firstHour - HOUR_FROM) * HOUR_PX;
  }, [start, firstHour, HOUR_FROM]);
  const hours = Array.from({ length: HOUR_TO - HOUR_FROM }, (_, i) => HOUR_FROM + i);

  return (
    <div className="cal-weekview" role="region" aria-label="Tydzień">
      <div className="cal-wk-head">
        <div />
        {week.map((day, i) => (
          <div key={day} className={`cal-wk-day${day === today ? ' today' : ''}${isPicked(day, interact) ? ' selected-day' : ''}`} {...dayTarget(day, interact)}>
            <span className="cal-wk-dow">{WEEKDAYS_SHORT[i]}</span>
            <span className="cal-day-num">{dayNumber(day)}</span>
            {dayExtra?.marks(day)}
          </div>
        ))}
      </div>
      <div className="cal-wk-band">
        <div className="cal-wk-band-label">cały dzień</div>
        <div className="cal-wk-band-days">
          <div className="cal-days" aria-hidden="true">
            {week.map((day) => (
              <div key={day} className={`cal-day${isPicked(day, interact) ? ' selected-day' : ''}`} {...dayTarget(day, interact)} />
            ))}
          </div>
          <div className="cal-lanes" style={{ gridTemplateRows: `0 repeat(${Math.max(lanes, 1)}, 20px)` }}>
            {segs.map((seg) => (
              <EventBar key={`${seg.event.id}:${seg.event.piece?.index ?? 0}`} seg={seg} week={week} selected={seg.event.id === selectedId} onOpen={onOpen} interact={interact} />
            ))}
          </div>
        </div>
      </div>
      <div className="cal-wk-scroll" ref={scroller}>
        <div className="cal-wk-grid" style={{ height: hours.length * HOUR_PX }}>
          <div className="cal-wk-hours" aria-hidden="true">
            {hours.map((h) => (
              <div key={h} style={{ height: HOUR_PX }}>
                {h}:00
              </div>
            ))}
          </div>
          {week.map((day) => (
            <div key={day} className={`cal-wk-col${day === today ? ' today' : ''}${isPicked(day, interact) ? ' selected-day' : ''}`} {...dayTarget(day, interact)}>
              {columns(timed.filter((t) => t.day === day)).map(({ item, col, cols }) => {
                const from = item.startMin;
                const to = Math.max(item.endMin, from + 30);
                const e = item.event;
                return (
                  <button
                    key={`${e.id}:${e.piece?.index ?? 0}`}
                    type="button"
                    className={`cal-slot${e.needsQuote ? ' needs-quote' : ''}${e.id === selectedId ? ' selected' : ''}${e.clientColor ? ' client-mark' : ''}${pickedClass(e.id, interact)}`}
                    data-status={e.status}
                    data-kind={e.kind}
                    data-event-id={e.id}
                    {...dragSource(e.id, interact, e.piece?.offsetDays)}
                    // a click on an event is not a click on its day
                    onDoubleClick={(ev) => ev.stopPropagation()}
                    style={{ top: ((from - HOUR_FROM * 60) / 60) * HOUR_PX, height: ((to - from) / 60) * HOUR_PX - 2, left: `${(col / cols) * 100}%`, width: `${100 / cols}%`, ...(e.clientColor ? markStyle(e.clientColor) : {}) }}
                    aria-label={interact.pickOnly ? pickLabel(e) : detailsLabel(e)}
                    title={interact.pickOnly ? eventName(e) : undefined}
                    // while picking dates the click goes on to the day's column, which picks the day (review I2)
                    onClick={(ev) => {
                      if (toggledEvent(ev, e.id, interact)) return;
                      if (!interact.pickOnly) onOpen(e.id, day);
                    }}
                  >
                    <Unstaffed on={e.unstaffed} />
                    <span className="cal-bar-name">{eventName(e)}</span>
                    <CrewInitials crew={e.crew} />
                    <span className="cal-slot-time">
                      {timeOf(e.startsAt) || '0:00'}–{item.endMin === 24 * 60 ? '24:00' : timeOf(e.endsAt)}
                    </span>
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/** "1 wydarzenie", "2 wydarzenia", "5 wydarzeń". */
function eventsCount(count: number): string {
  return count === 1 ? '1 wydarzenie' : count % 10 >= 2 && count % 10 <= 4 && (count % 100 < 12 || count % 100 > 14) ? `${count} wydarzenia` : `${count} wydarzeń`;
}

/**
 * Year: twelve small months; a busy day is shaded (darker when more happens), a needed quote adds a yellow dot.
 * A day with events is a button (owner remark 17): one event opens its card, several give a small list to pick from.
 */
export function YearView({
  anchor,
  events: all,
  today,
  onPickMonth,
  onOpen,
  ...interact
}: { anchor: string; events: CalendarEvent[]; today: string; onPickMonth: (day: string) => void; onOpen?: OnOpen | undefined } & Interact) {
  const events = eventPieces(withDates(all));
  const year = anchor.slice(0, 4);
  const load = busyDays(events);
  const [listed, setListed] = useState<string | null>(null);
  const on = (day: string) =>
    uniqueEvents(
      events.filter((e) => {
        const d = eventDays(e);
        return d.first <= day && d.last >= day;
      }),
    );
  const dayName = (day: string) => `${dayNumber(day)} ${MONTHS_GENITIVE[Number(day.slice(5, 7)) - 1]}`;
  return (
    <div className="cal-year" role="region" aria-label="Rok">
      {MONTHS.map((name, i) => {
        const first = `${year}-${String(i + 1).padStart(2, '0')}-01`;
        const month = first.slice(0, 7);
        const count = uniqueEvents(
          events.filter((e) => {
            if (isClosed(e.status)) return false;
            const d = eventDays(e);
            return d.first.slice(0, 7) <= month && d.last.slice(0, 7) >= month;
          }),
        ).length;
        const label = count === 0 ? '' : eventsCount(count);
        const listedHere = listed !== null && listed.slice(0, 7) === month ? on(listed) : [];
        return (
          <section key={name} className="cal-ymonth">
            <button type="button" className="cal-ymonth-title" onClick={() => onPickMonth(first)}>
              <span className={month === today.slice(0, 7) ? 'current' : undefined}>{name}</span>
              {label && <span className="cal-ymonth-count">{label}</span>}
            </button>
            <div className="cal-ygrid">
              {WEEKDAYS_SHORT.map((d) => (
                <span key={d} className="cal-ydow" aria-hidden="true">
                  {d.slice(0, 1)}
                </span>
              ))}
              {monthGrid(first)
                .flat()
                .map((day) => {
                  if (day.slice(0, 7) !== month) return <span key={day} aria-hidden="true" />;
                  const l = load.get(day);
                  const className = `cal-yday${l ? (l.count > 1 ? ' busy-2' : ' busy-1') : ''}${l?.needsQuote ? ' needs-quote' : ''}${day === today ? ' today' : ''}${isPicked(day, interact) ? ' selected-day' : ''}`;
                  const target = dayTarget(day, interact);
                  const list = onOpen ? on(day) : [];
                  if (list.length === 0) {
                    return (
                      <span key={day} className={className} aria-hidden="true" {...target}>
                        {dayNumber(day)}
                      </span>
                    );
                  }
                  return (
                    <button
                      key={day}
                      type="button"
                      className={className}
                      {...target}
                      aria-label={`${dayName(day)}: ${list.length === 1 ? eventName(list[0]!) : eventsCount(list.length)}`}
                      aria-expanded={list.length > 1 ? listed === day : undefined}
                      onClick={(ev) => {
                        if (justDragged) return;
                        const mods = { range: ev.shiftKey, toggle: isMacLike() ? ev.metaKey : ev.ctrlKey };
                        interact.onSelectDay?.(day, mods);
                        // Shift / Cmd only pick days (owner 2026-10-21 00:44); so does any click while the dates of a new event are picked (M1)
                        if (mods.range || mods.toggle || interact.pickOnly) return;
                        if (list.length === 1) {
                          setListed(null);
                          onOpen!(list[0]!.id, day);
                        } else setListed(listed === day ? null : day);
                      }}
                    >
                      {dayNumber(day)}
                    </button>
                  );
                })}
            </div>
            {listedHere.length > 0 && (
              <div
                className="cal-ypop"
                role="dialog"
                aria-label={`Wydarzenia ${dayName(listed!)}`}
              >
                <DialogLayer onClose={() => setListed(null)} />
                <ul>
                  {listedHere.map((e) => (
                    <li key={e.id}>
                      <button
                        type="button"
                        className={`cal-ypop-item${e.clientColor ? ' client-mark' : ''}`}
                        data-status={e.status}
                        style={e.clientColor ? markStyle(e.clientColor) : undefined}
                        onClick={() => {
                          setListed(null);
                          onOpen!(e.id, listed!);
                        }}
                      >
                        {eventName(e)}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}

/** Phone month: a compact grid of days with dots; the chosen day's events are listed below it. */
export function PhoneMonthView({ anchor, events: all, today, renderList, onNewOnDay, dayExtra }: { anchor: string; events: CalendarEvent[]; today: string; renderList: (events: CalendarEvent[], empty: string) => React.ReactNode; onNewOnDay?: ((day: string) => void) | undefined; dayExtra?: DayExtra | undefined }) {
  const events = eventPieces(withDates(all));
  const month = anchor.slice(0, 7);
  const [picked, setPicked] = useState<string>(today.slice(0, 7) === month ? today : `${month}-01`);
  useEffect(() => setPicked(today.slice(0, 7) === month ? today : `${month}-01`), [month, today]);
  const on = (day: string) =>
    uniqueEvents(
      events.filter((e) => {
        const d = eventDays(e);
        return d.first <= day && d.last >= day;
      }),
    );
  return (
    <div className="cal-phone-month" role="region" aria-label="Miesiąc">
      <div className="cal-weekdays" aria-hidden="true">
        {WEEKDAYS_SHORT.map((d) => (
          <div key={d}>{d.slice(0, 1)}</div>
        ))}
      </div>
      <div className="cal-pgrid">
        {monthGrid(anchor)
          .flat()
          .map((day) => {
            const list = on(day);
            return (
              <button
                key={day}
                type="button"
                className={`cal-pday${day.slice(0, 7) !== month ? ' outside' : ''}${day === today ? ' today' : ''}${day === picked ? ' picked' : ''}`}
                aria-pressed={day === picked}
                aria-label={list.length ? `${dayNumber(day)} – ${list.length} wyd.` : String(dayNumber(day))}
                onClick={() => setPicked(day)}
                onDoubleClick={onNewOnDay ? () => onNewOnDay(day) : undefined}
              >
                <span className="cal-day-num">{dayNumber(day)}</span>
                <span className="cal-dots" aria-hidden="true">
                  {list.slice(0, 3).map((e) =>
                    // owner 2026-10-29: a rental is a dot in its client's colour (grey without one)
                    monthKind(e) === 'dot' ? (
                      <i key={e.id} className={`rental${e.needsQuote ? ' needs-quote' : ''}`} data-status={e.status} style={{ '--dot': dotColor(e.clientColor) } as CSSProperties} />
                    ) : (
                      <i key={e.id} className={e.needsQuote ? 'needs-quote' : undefined} data-status={e.status} />
                    ),
                  )}
                  {dayExtra?.has(day) && <i className="task-dot" />}
                </span>
              </button>
            );
          })}
      </div>
      <div className="cal-pday-list">
        {dayExtra?.has(picked) && <div className="cal-phone-tasks">{dayExtra.marks(picked)}</div>}
        {renderList(on(picked), 'Tego dnia nic nie zaplanowano.')}
      </div>
    </div>
  );
}

/** Phone week: the seven days one under another, each with its events. */
export function PhoneWeekView({ anchor, events: all, today, renderList, dayExtra }: { anchor: string; events: CalendarEvent[]; today: string; renderList: (events: CalendarEvent[], empty: string) => React.ReactNode; dayExtra?: DayExtra | undefined }) {
  const events = eventPieces(withDates(all));
  const start = startOfWeek(anchor);
  return (
    <div className="cal-phone-week" role="region" aria-label="Tydzień">
      {Array.from({ length: 7 }, (_, i) => addDays(start, i)).map((day, i) => {
        const list = uniqueEvents(
          events.filter((e) => {
            const d = eventDays(e);
            return d.first <= day && d.last >= day;
          }),
        );
        return (
          <section key={day} className={`cal-agenda-day${day === today ? ' today' : ''}`}>
            <h3>
              <span className="cal-day-num">{dayNumber(day)}</span> {WEEKDAYS_SHORT[i]}
            </h3>
            {dayExtra?.has(day) && <div className="cal-phone-tasks">{dayExtra.marks(day)}</div>}
            {renderList(list, 'Nic nie zaplanowano.')}
          </section>
        );
      })}
    </div>
  );
}
